import { Button, Modal } from "antd";
import CheckIcon from "assets/images/icon/cert/check-white.svg";
import QrScanner from "qr-scanner";
import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { closeAlert, openAlert } from "state/alert/alertSlice";
import { useAppDispatch } from "state/hooks";
import { BaseModalProps } from "types/component";

interface ScanQRModalProps extends BaseModalProps {}

const ScanQRModal = (props: ScanQRModalProps) => {
  const { isOpen, toggleModal } = props;

  const dispatch = useAppDispatch();

  const scanner = useRef<QrScanner>();
  const qrBoxRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const [scanImageURL, setScanImageURL] = useState("");
  const [qrOn, setQrOn] = useState<boolean>(true);

  const handleScanSuccess = useCallback((url: string) => {
    window.location.replace(url);
  }, []);

  const handleScanFail = (err: string | Error) => {
    console.log(err);
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const image = acceptedFiles[0];
      const url = URL.createObjectURL(image);
      setScanImageURL(url);
      try {
        const result = await QrScanner.scanImage(url, {
          returnDetailedScanResult: true,
        });
        handleScanSuccess(result.data);
      } catch (error) {
        console.log("onDrop - error:", error);
      }
    },
    [handleScanSuccess],
  );

  const { getRootProps } = useDropzone({
    onDrop,
    accept: {
      "image/png": [".png", ".jpg", ".jpeg", ".webp"],
    },
    maxFiles: 1,
  });

  const onMobileChoose = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files as FileList;
    const acceptedTypes = ["image/png", "image/jpg", "image/jpeg", "image/webp"];
    const fileArr = Array.from(files);
    const isAllAccepted =
      files.length <= 1 && files?.length && fileArr.every((file) => acceptedTypes.includes(file.type));

    if (isAllAccepted) {
      const image = files[0];
      const url = URL.createObjectURL(image);
      setScanImageURL(url);

      try {
        const result = await QrScanner.scanImage(url, {
          returnDetailedScanResult: true,
        });
        handleScanSuccess(result.data);
      } catch (error) {
        console.log("onDrop - error:", error);
      }
    }

    document.getElementById("scan-qr-mobile")?.setAttribute("value", "");
    e.target.value = "";
  };

  useEffect(() => {
    if (!qrOn) {
      dispatch(
        openAlert({
          type: "error",
          title: "Xin vui lòng cho phép quyền sử dụng Camera để sử dụng tính năng này",
          onConfirm: () => {
            dispatch(closeAlert());
          },
        }),
      );
    }
  }, [dispatch, qrOn]);

  useEffect(() => {
    if (isOpen) {
      if (videoRef?.current && !scanner.current) {
        scanner.current = new QrScanner(videoRef.current, (result) => handleScanSuccess(result.data), {
          onDecodeError: handleScanFail,
          preferredCamera: "environment",
          overlay: qrBoxRef?.current || undefined,
          maxScansPerSecond: 1,
        });

        scanner?.current
          ?.start()
          .then(() => setQrOn(true))
          .catch((err) => {
            if (err) setQrOn(false);
          });
      }

      return () => {
        if (!videoRef) {
          scanner?.current?.stop();
        }
      };
    }
  }, [handleScanSuccess, isOpen]);

  useEffect(() => {
    return () => {
      URL.revokeObjectURL(scanImageURL);
    };
  }, [scanImageURL]);

  return (
    <Modal
      centered
      open={isOpen}
      onCancel={() => {
        toggleModal(false);
      }}
      footer={null}
      className="rounded-xl bg-white max-w-[90vw] md:max-w-[80vw] lg:max-w-[50vw]"
      width={600}
    >
      <div className="flex flex-col items-center py-5 relative">
        <h4 className="text-sm lg:text-md mb-5 lg:mb-8 text-center font-semibold">
          Vui lòng quét mã QR trên chứng nhận để tiến hành tra cứu thông tin
        </h4>

        <div
          ref={qrBoxRef}
          className="w-[80vw] h-auto md:w-full lg:w-[300px] lg:h-[300px] flex items-center justify-center p-2 relative"
        >
          <div className="w-[150px] h-[150px] lg:w-[300px] lg:h-[300px] absolute z-10">
            <div className="absolute w-[40px] h-[40px] top-0 left-0 border-solid border-t-2 lg:border-t-4 border-t-primary border-l-2 lg:border-l-4 border-l-primary rounded-tl-3xl"></div>
            <div className="absolute w-[40px] h-[40px] top-0 right-0 border-solid border-t-2 lg:border-t-4 border-t-primary border-r-2 lg:border-r-4 border-r-primary rounded-tr-3xl"></div>
            <div className="absolute w-[40px] h-[40px] bottom-0 left-0 border-solid border-b-2 lg:border-b-4 border-b-primary border-l-2 lg:border-l-4 border-l-primary rounded-bl-3xl"></div>
            <div className="absolute w-[40px] h-[40px] bottom-0 right-0 border-solid border-b-2 lg:border-b-4 border-b-primary border-r-2 lg:border-r-4 border-r-primary rounded-br-3xl"></div>
          </div>
          <video className="rounded-lg lg:rounded-none" ref={videoRef}></video>
        </div>

        <p className="my-2 text-dark-300">hoặc</p>
        <Button {...getRootProps()} className="btn btn-primary hidden lg:flex items-center justify-center">
          <img src={CheckIcon} alt="" className="mr-1 align-bottom w-[15px] h-[15px]" />
          Tải ảnh lên
        </Button>

        <Button className="btn btn-primary flex lg:hidden items-center justify-center">
          <img src={CheckIcon} alt="" className="mr-1 align-bottom w-[15px] h-[15px]" />
          Tải ảnh lên
          <input
            id="scan-qr-mobile"
            type="file"
            disabled={Boolean(scanImageURL)}
            onChange={onMobileChoose}
            className="absolute inset-0 opacity-0"
            multiple
            accept=".png, .jpg, .jpeg, .webp"
          />
        </Button>
      </div>
    </Modal>
  );
};

export default ScanQRModal;
